import { useState, useEffect, useRef } from "react";

/**
 * Auto-rotating product image carousel with fade transitions.
 * - Single image → static (no rotation, no dots)
 * - Multiple images → auto-rotates every 3s with dot indicators
 * - Pauses rotation on hover, speeds up cycling on hover for a "flip through" feel
 */
export default function ProductImageCarousel({ images, alt, className = "", testId = "product-carousel" }) {
  // De-dupe and drop falsy entries so single-image products never rotate through
  // "blank" or repeated slots (root cause of the "rotates into black frames" report)
  const list = Array.from(new Set(
    (Array.isArray(images) ? images : []).filter(u => typeof u === "string" && u.trim())
  ));
  const [index, setIndex] = useState(0);
  const [hovering, setHovering] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (list.length <= 1) return;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setIndex(i => (i + 1) % list.length);
    }, hovering ? 900 : 3000);
    return () => clearInterval(timerRef.current);
  }, [list.length, hovering]);

  if (list.length === 0) {
    return <div className={`bg-[#0A0A0A] ${className}`} data-testid={testId} />;
  }

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      data-testid={testId}
    >
      {list.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={i === 0 ? alt : `${alt} – alternate view`}
          loading="lazy"
          decoding="async"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
          style={{ transform: hovering && i === index ? "scale(1.04)" : "scale(1)", transition: "opacity 700ms, transform 700ms" }}
        />
      ))}
      {/* Static fallback (first image) so screen-readers + no-JS still see something meaningful */}
      <img
        src={list[0]}
        alt={alt}
        aria-hidden="true"
        className="w-full h-full object-cover opacity-0"
      />
      {list.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
          {list.map((src, i) => (
            <button
              key={src}
              aria-label={`Show image ${i + 1} of ${list.length}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIndex(i); }}
              data-testid={`${testId}-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? "bg-[#F7931E] w-6" : "bg-white/40 w-1.5 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
      )}
      {list.length > 1 && (
        <span className="absolute top-2 right-2 z-10 bg-black/70 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
          {list.length} photos
        </span>
      )}
    </div>
  );
}
